import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaShieldVirus, FaTimes, FaCheckCircle } from 'react-icons/fa';

const UGPandemicModule = ({ onBack }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalModule, setModalModule] = useState(null);
  const [form, setForm] = useState({ status: 'in_progress', activitiesCompleted: '', reflectionNotes: '' });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/pandemic`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setModules(data.modules || []);
    } catch (err) {
      console.error('Error fetching Pandemic modules:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const openModal = (m) => {
    setModalModule(m);
    setForm({
      status: m.status && m.status !== 'not_started' ? m.status : 'in_progress',
      activitiesCompleted: m.activitiesCompleted || '',
      reflectionNotes: m.reflectionNotes || ''
    });
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/pandemic/${modalModule.number}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalModule(null);
        await fetchModules();
      } else {
        alert(data.message || 'Failed to save progress');
      }
    } catch (err) {
      console.error('Error saving Pandemic module progress:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // module's progress alone does not flip its review status back to pending.
  const resubmit = async (moduleNumber) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/pandemic/${moduleNumber}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchModules();
      else alert(data.message || 'Failed to resubmit module');
    } catch (err) {
      console.error('Error resubmitting Pandemic module:', err);
    }
  };

  const completedCount = modules.filter(m => m.status === 'completed').length;

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaShieldVirus /> Pandemic Module</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        NMC's post-COVID CBME addition — taught longitudinally in Phase 3 Part 2 alongside AETCOM, covering triage, emergency/ICU management, palliative care, information management, and mental health during pandemics.
      </p>

      {loading ? (
        <div className="ug-due-empty">Loading Pandemic modules...</div>
      ) : (
        <div className="ug-topic-block">
          <div className="ug-topic-name">
            Phase 3 Part 2 <span style={{ float: 'right', textTransform: 'none', fontWeight: 400 }}>{completedCount}/{modules.length} completed</span>
          </div>
          {modules.map(m => (
            <div key={m.number} className="ug-aetcom-module">
              <div className="ug-competency-row">
                <div className="ug-competency-main">
                  <span className="ug-competency-code">{m.number} &middot; {m.department}</span>
                  <span className="ug-competency-text">{m.title}</span>
                </div>
                <div className="ug-competency-side">
                  {m.status === 'completed' ? (
                    <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Completed</span>
                  ) : m.status === 'in_progress' ? (
                    <span className="ug-status-badge ug-status-pending">In Progress</span>
                  ) : (
                    <span className="ug-status-badge ug-status-not-started">Not Started</span>
                  )}
                  {m.review?.status === 'approved' && (
                    <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>
                  )}
                  {m.review?.status === 'needs_revision' && (
                    <span className="ug-status-badge ug-status-revision">Needs revision</span>
                  )}
                </div>
              </div>
              <p className="ug-aetcom-module-hint">
                {m.activitiesCompleted || m.reflectionNotes
                  ? (m.activitiesCompleted || m.reflectionNotes)
                  : 'No progress recorded yet. Click "Update progress" to add notes.'}
              </p>
              {m.review?.feedback && (
                <div className="ug-aetcom-faculty-feedback">
                  <strong>{m.review.status === 'needs_revision' ? 'Needs revision — ' : ''}Faculty feedback:</strong> {m.review.feedback}
                </div>
              )}
              {m.review?.reviewerName && (
                <p className="ug-reviewer-tag">
                  {m.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {m.review.reviewerName}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                {m.review?.status !== 'approved' && (
                  <button className="ug-log-btn" onClick={() => openModal(m)}>Update progress</button>
                )}
                {m.review?.status === 'needs_revision' && (
                  <button className="ug-log-btn" onClick={() => resubmit(m.number)}>Resubmit for review</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalModule && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalModule(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Update progress</h3>
              <FaTimes onClick={() => !submitting && setModalModule(null)} />
            </div>
            <p className="ug-modal-competency">{modalModule.number} — {modalModule.title} ({modalModule.department})</p>

            <label className="ug-form-label">Status</label>
            <select
              className="ug-form-input"
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            <label className="ug-form-label">Activities completed</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              placeholder="e.g. Attended the triage simulation session, participated in the ICU case discussion..."
              value={form.activitiesCompleted}
              onChange={e => setForm({ ...form, activitiesCompleted: e.target.value })}
            />

            <label className="ug-form-label">Reflection notes</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              placeholder="What did you learn from this module? How will it change your practice?"
              value={form.reflectionNotes}
              onChange={e => setForm({ ...form, reflectionNotes: e.target.value })}
            />

            <label className="ug-form-label">Faculty feedback</label>
            <div className="ug-aetcom-faculty-feedback">
              {modalModule.review?.feedback || modalModule.facultyFeedback || 'Faculty feedback is filled by faculty only.'}
            </div>
            {modalModule.review?.reviewerName && (
              <p className="ug-reviewer-tag">
                {modalModule.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {modalModule.review.reviewerName}
              </p>
            )}

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalModule(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save progress'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGPandemicModule;
